export type MessageTree = string | MessageTree[] | { [key: string]: MessageTree };

type MessageContainer = MessageTree[] | { [key: string]: MessageTree };
type MessageCatalog = Readonly<Record<string, unknown>>;

function isNumericSegment(segment: string) {
  return /^\d+$/.test(segment);
}

function createContainer(segment: string): MessageContainer {
  return isNumericSegment(segment) ? [] : {};
}

function setNestedValue(container: MessageContainer | undefined, path: string[], value: string) {
  if (path.length === 0) {
    return value;
  }

  const root = container ?? createContainer(path[0]);
  let current: MessageContainer = root;

  for (let index = 0; index < path.length; index += 1) {
    const segment = path[index];
    const lastSegment = index === path.length - 1;
    const nextSegment = path[index + 1];
    const key = Array.isArray(current) ? Number(segment) : segment;

    if (lastSegment) {
      current[key] = value;
      continue;
    }

    const nextValue = current[key];

    if (!nextValue || typeof nextValue === 'string') {
      current[key] = createContainer(nextSegment);
    }

    current = current[key] as MessageContainer;
  }

  return root;
}

export function buildMessageTree(messages: MessageCatalog, id: string): MessageTree | undefined {
  return buildTypedMessageTree(messages, id);
}

export function buildTypedMessageTree<TValue extends MessageTree>(messages: MessageCatalog, id: string): TValue | undefined {
  const directMessage = messages[id];
  const nestedEntries = Object.entries(messages).filter(([key, value]) => typeof value === 'string' && key.startsWith(`${id}.`));

  if (nestedEntries.length === 0) {
    return (typeof directMessage === 'string' ? directMessage : undefined) as TValue | undefined;
  }

  let tree: MessageContainer | undefined;

  for (const [key, value] of nestedEntries) {
    const relativePath = key.slice(id.length + 1).split('.');
    tree = setNestedValue(tree, relativePath, value);
  }

  return tree as TValue;
}
